import { t, Trans } from '@lingui/macro';
import _ from 'lodash';
import { useFormContext } from 'react-hook-form';
import { Box, MenuItem } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { i18n } from '@lingui/core';
import { useEffect, useState } from 'react';
import FormSection from '../../form/FormSection';
import NgpToggle from '../../form/NgpToggle';
import {
    AGENT_CERTIFICATE_FIELD,
    AGENT_CERTIFICATE_NAME_FIELD,
    AGENT_WIRED_AUTH_ENABLED,
    AGENT_WIRED_FIELD,
    AGENTS_FUNCTION_NAME,
    GLOBAL_AGENT_SETTINGS_FIELD,
} from './AgentConstants';
import NgpSelect from '../../form/NgpSelect';
import { buildCertDataSource, buildCertificateColumns, buildWiredNetworkOptions } from './AgentDetailHelper';
import { AUTH_CERTIFICATE_FIELD, EAP_TYPE_FIELD, EAP_TYPE_OPTIONS } from '../wired/WiredNetworksConstants';
import { CERTIFICATE_USAGES } from '../certificates/CertificateConstants';
import { useArrayLoader } from '../../data-table/Loaders';
import DataTable from '../../data-table/DataTable';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';
import { SORT_DESC } from '../../../utilities/FormUtilities';
import { useDataService } from '../../../services/ApiDataService';

const listRefreshInterval = 30000;

const initialCertSort = [{ property: AGENT_CERTIFICATE_NAME_FIELD, direction: SORT_DESC }];
const noCertificateText = i18n._(
    t`Note: No certificate configured. Please configure the certificate for the selected Wired network, or select the certificate for nPoint.`
);

export function AgentDetailWiredAuth({
    formData,
    classes,
    isHardware3kAgent,
    isHardwareEdgeSensorAgent,
    configuredWiredNetworks,
    latestAgentData,
}) {
    const { watch, getValues } = useFormContext();
    const dataService = useDataService();
    const [wiredNPointLevelCert, setWiredNPointLevelCert] = useState([]);
    const [wiredNetworkCert, setWiredNetworkCert] = useState([]);
    const [showAssociatedCert, setShowAssociatedCert] = useState([]);
    const [showNoCertificateText, setShowNoCertificateText] = useState([]);
    const agentWiredAuthEnabled = watch(AGENT_WIRED_AUTH_ENABLED);
    const wiredSelection = watch(AGENT_WIRED_FIELD);

    const globalBeaconSettings = formData[GLOBAL_AGENT_SETTINGS_FIELD];
    const showWiredAuthSection =
        (isHardware3kAgent.current || isHardwareEdgeSensorAgent.current) &&
        _.get(globalBeaconSettings, 'wiredAuthenticationGlobal', false);
    const wiredNetworkOptions = buildWiredNetworkOptions(configuredWiredNetworks.current, formData);

    const { triggerRef } = useIntervalRefreshTrigger({
        interval: listRefreshInterval,
    });

    const certificateColumns = buildCertificateColumns();

    const selectedWiredNetwork = wiredNetworkOptions.find((network) => network._id === wiredSelection);
    const isEapTypeTls = _.get(selectedWiredNetwork, EAP_TYPE_FIELD) === EAP_TYPE_OPTIONS.eapTls;
    const currentWired = getValues(AGENT_WIRED_FIELD);

    useEffect(() => {
        (async () => {
            const { url: nPointLevelUrl, params: nPointLevelParams } = buildCertDataSource(
                _.get(latestAgentData, AGENT_CERTIFICATE_FIELD),
                CERTIFICATE_USAGES.wired
            );
            const wiredNPointLevelCertData = await dataService.get(nPointLevelUrl, nPointLevelParams);
            let wiredNetworkCertData = null;
            if (_.get(selectedWiredNetwork, AUTH_CERTIFICATE_FIELD)) {
                const { url: wiredNetworkUrl, params: wiredNetworkParams } = buildCertDataSource(
                    [_.get(selectedWiredNetwork, AUTH_CERTIFICATE_FIELD)],
                    CERTIFICATE_USAGES.wired
                );
                wiredNetworkCertData = await dataService.get(wiredNetworkUrl, wiredNetworkParams);
            }

            setWiredNPointLevelCert(wiredNPointLevelCertData?.data);
            setWiredNetworkCert(wiredNetworkCertData?.data);

            setShowAssociatedCert(
                isEapTypeTls &&
                    ((!_.isNil(wiredNPointLevelCertData?.data) && wiredNPointLevelCertData?.data.length !== 0) ||
                        (!_.isNil(wiredNetworkCertData?.data) && wiredNetworkCertData?.data.length !== 0))
            );

            setShowNoCertificateText(
                isEapTypeTls &&
                    (_.isNil(wiredNPointLevelCertData?.data) || wiredNPointLevelCertData?.data.length === 0) &&
                    (_.isNil(wiredNetworkCertData?.data) || wiredNetworkCertData?.data.length === 0)
            );
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentWired]);

    const certLoader = useArrayLoader(
        !_.isNil(wiredNPointLevelCert) && wiredNPointLevelCert.length !== 0 ? wiredNPointLevelCert : wiredNetworkCert,
        (data) => data
    );

    return (
        <>
            {showWiredAuthSection && (
                <FormSection title={<Trans>Wired Authentication</Trans>}>
                    <NgpToggle fieldName={AGENT_WIRED_AUTH_ENABLED} label={<Trans>Use 802.1x Authentication</Trans>} />

                    {agentWiredAuthEnabled && (
                        <>
                            <NgpSelect
                                fieldName={AGENT_WIRED_FIELD}
                                label={<Trans>Assigned Wired Network</Trans>}
                                required
                                className={classes.genericSelect}
                            >
                                {_.map(wiredNetworkOptions, (network) => (
                                    <MenuItem key={network._id} value={network._id}>
                                        {network.nameFmt}
                                    </MenuItem>
                                ))}
                            </NgpSelect>

                            <Box mt={3}>
                                {showNoCertificateText && (
                                    <Typography variant="body2">
                                        <b>{noCertificateText}</b>
                                    </Typography>
                                )}
                            </Box>

                            {showAssociatedCert && (
                                <FormSection title={<Trans>Associated Wired Certificates</Trans>}>
                                    <DataTable
                                        tableId={`${AGENTS_FUNCTION_NAME}-certificate`}
                                        triggerRef={triggerRef}
                                        loader={certLoader}
                                        columns={certificateColumns}
                                        sorters={initialCertSort}
                                        visibleRows={10}
                                        disableCSVExport
                                        disableColumnRemover
                                        showToolbar={false}
                                        showSearch={false}
                                    />
                                </FormSection>
                            )}
                        </>
                    )}
                </FormSection>
            )}
        </>
    );
}
